import React, { useState, useEffect } from 'react';
import { Route, Routes, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { createTheme, ThemeProvider } from '@mui/material/styles'; 
import SignupForm from './Components/Signup/SignupForm';
import LoginForm from './Components/Login/LoginForm';
import ForgotPasswordForm from './Components/Login/ForgotPasswordForm';
import VerifyCodeForm from './Components/Login/VerifyCodeForm';
import ResetPasswordForm from './Components/Login/ResetPasswordForm';
import UserPanel from './Components/UserPanel/UserPanel';
import AdminPanel from './Components/Admin/AdminPanel';
import HomePage from './Components/Webpage/Homepage';
import PlanDetailsPage from './Components/PlanDetailsPage/PlanDetailsPage';
import AddAccountPage from './Components/PlanDetailsPage/AddAccountPage';
import ConfirmPurchasePage from './Components/PlanDetailsPage/ConfirmPurchasePage';
import PlanSettingsPage from './Components/PlanDetailsPage/PlanSettingsPage';
import PlansPage from './Components/Webpage/PlansPage';
import FloatingSupportButton from './Components/FloatingSupportButton/FloatingSupportButton';
import RulesPage from './Components/Webpage/RulesPage';
import FaqPage from './Components/Faq/FAQ';
import AboutPage from './Components/About/AboutPage';
import PredictionPage from './Components/PredictionPage/PredictionPage'; 
import InvoiceCallback from './Components/UserPanel/InvoiceCallback';
import './styles/globals.scss';




const App = () => {
  const isAuthenticated = localStorage.getItem('isAuthenticated') === 'true';
  const isAdmin = localStorage.getItem('isAdmin') === 'true';

  const location = useLocation();
  const navigate = useNavigate();
  
  const muiTheme = createTheme({
    typography: {
      fontFamily: [
        'Vazirmatn',
        'Roboto',
        '"Helvetica Neue"',
        'Arial',
        'sans-serif'
      ].join(','),
    },
    direction: 'rtl',
  });

  const [theme, setTheme] = useState<'light' | 'dark'>(() => {
    const queryParams = new URLSearchParams(location.search);
    const themeFromUrl = queryParams.get('theme');
    if (themeFromUrl === 'light' || themeFromUrl === 'dark') {
      localStorage.setItem('theme', themeFromUrl);
      return themeFromUrl;
    }
    const storedTheme = localStorage.getItem('theme');
    return (storedTheme === 'light' || storedTheme === 'dark') ? storedTheme : 'dark';
  });

  const toggleTheme = () => {
    const newThemeName = theme === 'dark' ? 'light' : 'dark';
    setTheme(newThemeName);
    localStorage.setItem('theme', newThemeName);
    const queryParams = new URLSearchParams(location.search);
    queryParams.set('theme', newThemeName);
    navigate(`${location.pathname}?${queryParams.toString()}`, { replace: true });
  };

  useEffect(() => {
    document.body.className = theme;
    const queryParams = new URLSearchParams(location.search);
    if (queryParams.get('theme') !== theme) {
      queryParams.set('theme', theme);
      navigate(`${location.pathname}?${queryParams.toString()}`, { replace: true });
    }
  }, [theme, location.pathname,location.search, navigate]);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const themeFromUrl = queryParams.get('theme');
    if (themeFromUrl && (themeFromUrl === 'light' || themeFromUrl === 'dark') && themeFromUrl !== theme) {
      setTheme(themeFromUrl);
      localStorage.setItem('theme', themeFromUrl);
    }
  }, [location.search]);

  const handleLogout = () => {
    localStorage.removeItem('isAuthenticated');
    localStorage.removeItem('token');
    localStorage.removeItem('isAdmin');
    navigate('/');
  };

  const LoginPageWrapper = () => ( <div className="page-center-container"><div className="glassy-form-container"><LoginForm /></div></div> );
  const SignupPageWrapper = () => ( <div className="page-center-container"><div className="glassy-form-container"><SignupForm /></div></div> );
  const ForgotPasswordPageWrapper = () => ( <div className="page-center-container"><div className="glassy-form-container"><ForgotPasswordForm switchToLogin={() => { navigate("/login"); }} /></div></div> );
  const VerifyCodePageWrapper = () => ( <div className="page-center-container"><div className="glassy-form-container"><VerifyCodeForm /></div></div> );
  const ResetPasswordPageWrapper = () => ( <div className="page-center-container"><div className="glassy-form-container"><ResetPasswordForm /></div></div> );
  const AdminPanelWrapper = () => <AdminPanel theme={theme} />;
  const loggedInRedirectPath = '/dashboard';

  return (
    <ThemeProvider theme={muiTheme}>
      <div className={`app-bg ${theme}`}>
        <Routes>
          <Route
            path="/"
            element={<HomePage isAuthenticated={isAuthenticated} theme={theme} toggleTheme={toggleTheme} />}
          />
          <Route path="/login" element={!isAuthenticated ? <LoginPageWrapper /> : <Navigate to={loggedInRedirectPath} />} />
          <Route path="/signup" element={!isAuthenticated ? <SignupPageWrapper /> : <Navigate to={loggedInRedirectPath} />} />
          <Route path="/forgot-password" element={!isAuthenticated ? <ForgotPasswordPageWrapper /> : <Navigate to={loggedInRedirectPath} />} />
          <Route path="/verify-reset-code" element={!isAuthenticated ? <VerifyCodePageWrapper /> : <Navigate to={loggedInRedirectPath} />} />
          <Route path="/reset-password" element={!isAuthenticated ? <ResetPasswordPageWrapper /> : <Navigate to={loggedInRedirectPath} />} />

          <Route path="/plan-details/:planId" element={<PlanDetailsPage />} />
          <Route path="/plans" element={<PlansPage isAuthenticated={isAuthenticated} theme={theme} />} />
          <Route
            path="/add-account"
            element={isAuthenticated ? <AddAccountPage /> : <Navigate to="/login" state={{ from: location.pathname + location.search }} />}
          />
          <Route
            path="/confirm-purchase"
            element={isAuthenticated ? <ConfirmPurchasePage /> : <Navigate to="/login" state={{ from: location.pathname + location.search }} />}
          />
          <Route path="/plan-settings" element={isAuthenticated ? <PlanSettingsPage />: <Navigate to="/login" />} />
          <Route path="/rules" element={<RulesPage theme={theme} />} />
          <Route path="/faq" element={<FaqPage theme={theme} />} />
          
          <Route path="/about" element={<AboutPage />} />
          <Route path="/prediction" element={<PredictionPage theme={theme} />} />

          <Route path="/admin/*" element={isAuthenticated && isAdmin ? <AdminPanelWrapper /> : <Navigate to="/login" />} />
          
          <Route
            path="/dashboard/*"
            element={
              isAuthenticated
                ? <UserPanel handleLogout={handleLogout} theme={theme} isAdmin={isAdmin} />
                : <Navigate to="/login" />
            }
          />
          <Route path='/invoice/plan/callback' element={<InvoiceCallback id={parseInt((new URLSearchParams(location.search)).get("id"))}/>}/>
        </Routes>
        <FloatingSupportButton theme={theme} />
      </div>
    </ThemeProvider>
  );
};

export default App;